import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { COLOR_SYSTEM } from '../../design/design-system';
import { AIReview, AIReviewStatus, DrugSearchResult } from '../../../common/models/types';
import { getPillsByItemCodes } from '../../../api/drugService';
import { log, LogLevel } from 'utils/LogUtil';
import PrintSidePanel from './PrintSidePanel';
import PrintContent from './PrintContent';
import { ChangePill, useChangeNotice } from './changeNotice';
import { calculateAge } from '../../../utils/TimeUtil';
import { checkSectionExistence } from '../../../utils/sectionParser';
import {
  computeOptionAvailability,
  extractMentionedItemCodes,
  getMentionedAdditions,
} from '../../../utils/prescriptionPrintScope';

const PrintStyles = createGlobalStyle`
  @media print {
    @page {
      size: A4;
      margin: 20mm;  /* 모든 페이지에 20mm 여백 적용 (자동 페이지 넘김 포함) */
    }

    body {
      margin: 0;
      padding: 0;
      background: white;
    }

    html, body {
      height: auto !important;
      overflow: visible !important;
    }

    * {
      overflow: visible !important;
      /* 배경색, 하이라이트, blockquote 등이 인쇄 시에도 표시되도록 설정 */
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
  }
`;

const Container = styled.div`
  display: flex;
  height: 100vh;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};

  @media print {
    height: auto;
    display: block;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  background: ${COLOR_SYSTEM.get("Gray")?.[100]};
  padding: 0;

  @media print {
    width: 100%;
    height: auto !important;
    overflow: visible !important;
    background: white;
    display: block;
  }
`;

/* 미리보기 영역 래퍼 — 가장자리 알림 알약을 스크롤과 무관하게 고정하기 위한 기준 */
const PreviewWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;

  @media print {
    display: block;
  }
`;

const SidePanel = styled.div`
  width: 320px;
  background: white;
  border-left: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  display: flex;
  flex-direction: column;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.05);

  @media print {
    display: none;
  }
`;

export interface PrintOptions {
  showPatientInfo: boolean;
  showPrescriptionTable: boolean;
  prescriptionViewMode: 'date' | 'hospital';
  referenceMode: 'none' | 'button' | 'include';
  showDrugImages: boolean;
  hidePharmacistReport: boolean;
  hidePatientGuide: boolean;
  /**
   * 복용약 범위 (설계: docs/ai-discussions/20260904-print-drug-scope-filter.md)
   * - 라디오: 'all' = 전체 처방약, 'ongoing' = 현재 복용중인 약(오늘 기준 복용기간 내)
   * - drugScopeAddMentioned는 ongoing의 부가 옵션 — 보고서에 언급된(복용중이 아닌) 약을 목록에 추가.
   *   'all'에서는 무의미(전량 표시)라 무시된다
   * - 범위는 환자·인쇄 건마다 달라지는 판단이라 localStorage에 저장하지 않는다 (매 진입 'all')
   */
  drugScope: 'all' | 'ongoing';
  drugScopeAddMentioned: boolean;
  /**
   * 언급 약 참고 섹션의 표시 방식 — 'drug' = 약만 간단히(참고 표, 품목당 1행),
   * 'group' = 그 약의 마지막 처방전 통째(병용약 맥락으로 용도 추정 가능, 약사 관점)
   */
  drugScopeMentionedStyle: 'drug' | 'group';
  /**
   * 행 단위 "종료 예상" 배지 표시 여부 — 전체/복용중 범위 공통. 표시 취향이라 localStorage에
   * 저장·유지된다(범위 필드와 달리 환자 간 잔존해도 안전 문제 없음 — 배지는 부가 정보).
   */
  markEndedDrugs: boolean;
  /**
   * 본문 의약품 카드(사진·성상·각인) 크기 — 환자용 복약지도문 하위 옵션.
   * 'none'은 카드 자체를 숨김. 'large'가 기존 인쇄 확대 규칙(이미지 200px·제품명 16px)과 동일.
   * 표시 취향이라 저장·유지.
   */
  patientGuideCardSize: 'none' | 'small' | 'medium' | 'large';
  /**
   * 처방표 약물명 아래 분류 표기 — 'meft' = 약효분류번호+분류명(예: 214 혈압강하제),
   * 'atc' = ATC 코드+대분류명(예: C09CA01 심혈관계). 결측 행은 표기 생략. 표시 취향이라 저장·유지.
   */
  classificationDisplay: 'none' | 'meft' | 'atc';
}

/** 범위 필드의 진입 기본값 — 비저장 정책이라 항상 이 값으로 시작한다 */
const DEFAULT_DRUG_SCOPE = {
  drugScope: 'all' as const,
  drugScopeAddMentioned: false,
  drugScopeMentionedStyle: 'drug' as const,
};

interface AIReviewPrintTemplateProps {
  review: AIReview;
}

const AIReviewPrintTemplate: React.FC<AIReviewPrintTemplateProps> = ({ review }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  
  // localStorage에서 기본값 로드
  const loadOptionsFromStorage = (): PrintOptions => {
    const saved = localStorage.getItem('print_options');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // 참고문헌은 항상 'none'으로 강제 설정
        return {
          ...parsed,
          referenceMode: 'none',
          showDrugImages: parsed.showDrugImages ?? false,
          hidePharmacistReport: parsed.hidePharmacistReport ?? false,
          hidePatientGuide: parsed.hidePatientGuide ?? false,
          markEndedDrugs: parsed.markEndedDrugs ?? true,
          patientGuideCardSize: parsed.patientGuideCardSize ?? 'large',
          classificationDisplay: parsed.classificationDisplay ?? 'none',
          // 범위 필드는 저장값을 무시하고 항상 'all'로 시작 (비저장 정책)
          ...DEFAULT_DRUG_SCOPE
        };
      } catch (e) {
        console.error('Failed to parse saved print options:', e);
      }
    }
    // 기본값: 환자정보 포함, 날짜별 보기, 참고문헌 제외, 의약품 이미지 미표시, 섹션 모두 표시
    return {
      showPatientInfo: true,
      showPrescriptionTable: true,
      prescriptionViewMode: 'date',
      referenceMode: 'none',
      showDrugImages: false,
      hidePharmacistReport: false,
      hidePatientGuide: false,
      markEndedDrugs: true,
      patientGuideCardSize: 'large',
      classificationDisplay: 'none',
      ...DEFAULT_DRUG_SCOPE
    };
  };

  const [printOptions, setPrintOptions] = useState<PrintOptions>(loadOptionsFromStorage());

  // 가장자리 알림 — 변경이 미리보기 뷰포트 밖에서 일어났을 때만 잠깐 표시 (공유 훅)
  const {
    containerRef: contentAreaRef,
    changePill,
    recordChange,
    handlePillClick,
  } = useChangeNotice(printOptions);

  // 옵션 변경 시 localStorage에 저장 — 범위 필드는 제외(비저장 정책)
  useEffect(() => {
    const { drugScope, drugScopeAddMentioned, drugScopeMentionedStyle, ...persisted } = printOptions;
    localStorage.setItem('print_options', JSON.stringify(persisted));
  }, [printOptions]);

  const handleOptionChange = (option: keyof PrintOptions, value: any) => {
    // 참고문헌 옵션은 무시하고 항상 'none'으로 유지
    if (option === 'referenceMode') {
      return;
    }
    // 변경이 반영될 섹션을 기록 — 렌더 후 뷰포트 밖이면 가장자리 알림을 띄운다
    recordChange(option);
    setPrintOptions(prev => ({
      ...prev,
      [option]: value
    }));
  };

  // 환자 정보 처리
  const patient = review.patientSnap;

  const handlePrint = () => {
    // PDF 저장 시 기본 파일명 지정: 브라우저는 인쇄 대화상자의 기본 파일명으로 document.title을 사용
    const patientNo = patient?.id ?? review.patientId ?? '';
    const reportNo = review.id;
    const originalTitle = document.title;
    const filename = `PhAI_환자${patientNo}_보고서${reportNo}`;
    document.title = filename;

    const restore = () => {
      document.title = originalTitle;
      window.removeEventListener('afterprint', restore);
    };
    window.addEventListener('afterprint', restore);

    window.print();
  };

  const age = patient?.age ? String(patient.age) + '세' : '-';

  // 의약품 + 건기식 통합 drugsData
  const drugsData = useMemo(() => [
    ...(review.referenceSnap?.drugs || []),
    ...(review.referenceSnap?.hff || []),
  ], [review.referenceSnap?.drugs, review.referenceSnap?.hff]);

  // pillsData 상태 (식별정보: 성상, 각인 등)
  const [pillsData, setPillsData] = useState<DrugSearchResult[]>([]);

  // drugsData에서 itemCodes 추출하여 pillsData 조회
  useEffect(() => {
    const fetchPillsData = async () => {
      if (drugsData.length === 0) {
        setPillsData([]);
        return;
      }

      const itemCodes = drugsData.map(d => d.itemCode).filter(Boolean);
      if (itemCodes.length === 0) {
        setPillsData([]);
        return;
      }

      try {
        const result = await getPillsByItemCodes(itemCodes);
        setPillsData(result);
        log(LogLevel.INFO, "[Print] pillsData 조회 완료", { count: result.length });
      } catch (error) {
        log(LogLevel.ERROR, "[Print] pillsData 조회 실패", error);
        setPillsData([]);
      }
    };

    fetchPillsData();
  }, [drugsData]);

  // 섹션 존재 여부 확인
  const sectionExistence = useMemo(() => {
    // 편집본이 있으면 편집본, 없으면 원본
    const content = review.reviewResultEdited || review.reviewResult || '';
    return checkSectionExistence(content);
  }, [review.reviewResult, review.reviewResultEdited]);

  // 보고서에 언급된(⑆img/편집본 itemCode) 약 집합 — 범위 필터·패널 카운트에 사용
  const mentionedItemCodes = useMemo(() => {
    const content = review.reviewResultEdited || review.reviewResult || '';
    return extractMentionedItemCodes(content);
  }, [review.reviewResult, review.reviewResultEdited]);

  // "보고서 언급으로 추가될 약" 목록 — 복용중이 아니면서 언급된 처방 품목(복용중 포함 품목은 제외).
  // 패널의 "N종 추가" 라벨·툴팁 약 목록에 쓰인다
  const mentionedAdditions = useMemo(
    () => getMentionedAdditions(patient?.prescriptionGroups || [], mentionedItemCodes, new Date()),
    [patient?.prescriptionGroups, mentionedItemCodes]
  );

  // 옵션별 "효과 있음" 판정 — 켜도 지면이 안 바뀔 옵션을 패널에서 비활성+사유로 미리 알린다
  // (옵션을 눌렀는데 아무 변화가 없어 "가만있어 보이는" 혼란 방지. 탑재뷰와 판정 공유)
  const optionAvailability = useMemo(
    () =>
      computeOptionAvailability(
        patient?.prescriptionGroups || [],
        mentionedItemCodes,
        new Date()
      ),
    [patient?.prescriptionGroups, mentionedItemCodes]
  );

  return (
    <>
      <PrintStyles />
      <Container>
        <PreviewWrap>
        <ContentArea ref={contentAreaRef}>
          <PrintContent
            ref={contentRef}
            review={review}
            patient={patient}
            age={age}
            printOptions={printOptions}
            drugsData={drugsData}
            pillsData={pillsData}
            mentionedAdditions={mentionedAdditions}
          />
        </ContentArea>
        {changePill && (
          <ChangePill $direction={changePill.direction} onClick={handlePillClick}>
            {changePill.direction === 'up' ? '↑ 위쪽 내용이 변경되었습니다' : '↓ 아래쪽 내용이 변경되었습니다'}
          </ChangePill>
        )}
        </PreviewWrap>
        <SidePanel>
          <PrintSidePanel
            printOptions={printOptions}
            onOptionChange={handleOptionChange}
            onPrint={handlePrint}
            sectionExistence={sectionExistence}
            showDrugScope
            mentionedAdditions={mentionedAdditions}
            optionAvailability={optionAvailability}
          />
        </SidePanel>
      </Container>
    </>
  );
};

export default AIReviewPrintTemplate;